import { Modal, Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { IDictionary } from '~utilities/IDictionary';
import './loan-payment-abas.less';
import { currencyFormatter } from '~utilities/formatters';
import { Key, TableRowSelection } from 'antd/lib/table/interface';
import { Dispatch } from 'redux';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import { loanPaymentAbasUnallocatedWarehouseLoanTransactionsSelector } from './selectors';
import {
    loanPaymentAbasAddAction,
    loanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction,
} from './actions';
import ICheckboxProps from '~utilities/ICheckboxProps';

export default function AddModal(props: {
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { onCancel, isOpen } = props;

    const [ selectedRowKeys, setSelectedRowKeys ] = useState<Key[]>([]);

    const unallocatedTransactions: IDictionary<IWarehouseLoanTransaction> = useSelector(loanPaymentAbasUnallocatedWarehouseLoanTransactionsSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!unallocatedTransactions) {
            dispatch(loanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction());
        }
    }, [
        dispatch,
        unallocatedTransactions,
    ]);

    const onClickOk: () => void = useCallback(() => {
        dispatch(loanPaymentAbasAddAction(selectedRowKeys.map((selectedRowKey: Key) => selectedRowKey.toString())));
        onCancel();
    }, [
        dispatch,
        onCancel,
        selectedRowKeys,
    ]);

    const onChangeSelectedRows: (rowKeys: Key[]) => void = useCallback((rowKeys: Key[]) => {
        setSelectedRowKeys(rowKeys);
    }, []);

    const getCheckboxProps: (row: IWarehouseLoanTransaction) => ICheckboxProps = useCallback((row: IWarehouseLoanTransaction) => ({
        className: null,
        disabled: false,
        key: row.loanTransactionUuid,
    }), []);

    if (!unallocatedTransactions) {
        return (
            <Modal
                okText='Add'
                open={isOpen}
                title='Add Loan Payment ABA'
                width={1000}
                wrapClassName='loan-payment-abas-add-modal'
            >
                <Spin />
            </Modal>
        );
    }

    const rowSelection: TableRowSelection<IWarehouseLoanTransaction> = {
        getCheckboxProps,
        onChange: onChangeSelectedRows,
        selectedRowKeys,
    };

    const columns: ColumnsType<IWarehouseLoanTransaction> = [
        {
            dataIndex: 'warehouseLoanTransactionTime',
            defaultSortOrder: 'descend',
            render: (warehouseLoanTransactionTime: string) => dayjs(warehouseLoanTransactionTime).format('DD/MM/YYYY HH:mm:ss'),
            sorter: (a: IWarehouseLoanTransaction, b: IWarehouseLoanTransaction) => dayjs(a.transactionTime) > dayjs(b.transactionTime) ? 1 : -1,
            title: 'Transaction Time',
            width: 200,
        },
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IWarehouseLoanTransaction): ReactNode => <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>,
            title: 'Loan',
        },
        {
            dataIndex: 'amountPrepaidInterest',
            render: (amountPrepaidInterest: number) => currencyFormatter.format(amountPrepaidInterest * -1),
            title: 'Interest',
            width: 200,
        },
    ];

    return (
        <Modal
            okButtonProps={{ disabled: !selectedRowKeys.length }}
            okText='Add'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Add Loan Payment ABA'
            width={1000}
            wrapClassName='loan-payment-abas-add-modal'
        >
            <Table
                columns={columns}
                dataSource={_.values(unallocatedTransactions)}
                pagination={false}
                rowKey='loanTransactionUuid'
                rowSelection={rowSelection}
                scroll={{ y: 600 }}
                size='middle'
            />
        </Modal>
    );
}
